import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import type { ObjectSchema } from 'yup';
import { getApplicationFormInfo, postApplication } from '@/api/Rover/ApplicationForm';
import type { IPostApplicationServicePayload } from '@/api/Rover/ApplicationForm/types';
import Button from '@/components/UI/Button';
import { JsonForm } from '@/components/UI/JsonForm';
import Loading from '@/components/UI/loading';
import { ApplicationFormProvider, useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
import { formConfigurationMapping, standardStepSchema, standardSteps } from '@/features/ApplicationForm/FormConfig';
import { EFormConfigType, type IApplicationFormStep } from '@/features/ApplicationForm/types';
import PageNotFound from '@/pages/PageNotFound';
import ApplicationFormSections from './ApplicationFormSections';
import { generateInitialValues } from './initialValuesGenerator';
import MultiStepHeader from './MultiStepHeader';
import SubmitSuccess from './SubmitSuccess';

interface IApplicationFormBaseProps {
  applicationFormId: string;
  steps: IApplicationFormStep[];
  initialValues?: Record<string, any>;
  validationSchema?: ObjectSchema<any>[];
  onSuccess?: ((data?: unknown, variables?: undefined, context?: unknown | undefined) => Promise<unknown> | undefined) | (() => void);
}

const ApplicationFormBase = (props: IApplicationFormBaseProps) => {
  const { applicationFormId, steps, validationSchema, initialValues } = props;

  const { currentStep, setCurrentStep, completedSteps, updateData, data, setFormJSON } = useApplicationFormContext();

  useEffect(() => {
    if (steps) {
      setFormJSON(steps);
    }
    return () => {
      setFormJSON([]);
    };
  }, [steps, setFormJSON]);

  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;

  const increaseStep = () => {
    if (!isLast) {
      setCurrentStep((prev) => prev + 1);
    }
  };
  const decreaseStep = () => {
    if (!isFirst) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleNext = (values: any) => {
    updateData(values);
    increaseStep();
  };

  const submitApplication = useMutation((data: IPostApplicationServicePayload) => postApplication({ data }), {
    onSuccess: (_data: any) => {
      if (props.onSuccess) {
        props.onSuccess();
      }
    },
  });

  const onSubmitHandler = (values: Record<string, any>) => {
    if (!isLast) {
      handleNext(values);
    } else {
      const applicationData: IPostApplicationServicePayload = {
        formId: applicationFormId,
        contactName: values.applicantInfo.fullName,
        contactEmail: values.applicantInfo.contactEmail,
        applicationData: values,
      };
      submitApplication.mutate(applicationData);
    }
  };

  const currentFormStep = steps[currentStep];

  return (
    <div className='container w-full h-screen/80 mx-auto p-5 flex flex-col justify-between'>
      <MultiStepHeader steps={steps} currentStep={currentStep} />
      <div className='flex-1 space-y-6 lg:col-span-9 overflow-y-scroll'>
        <JsonForm
          onSubmit={onSubmitHandler}
          hookFormProviderProps={{
            defaultValues: initialValues,
            mode: 'all',
            resolver: validationSchema?.[currentStep] && yupResolver(validationSchema[currentStep]),
          }}
          formClassName='p-4'
        >
          {currentFormStep && (
            <div className='col-span-12 grid grid-cols-12 gap-4 w-full h-full'>
              <ApplicationFormSections step={currentFormStep} currentStep={currentStep} />
            </div>
          )}
          <div className='col-span-12 flex justify-end space-x-4 py-4 border-t'>
            {!isFirst && (
              <Button
                label='Previous'
                btnStyle='neutral'
                type='button'
                onClick={(e) => {
                  e.preventDefault();
                  decreaseStep();
                }}
              />
            )}
            <Button label={`${isLast ? 'Submit' : 'Next'}`} btnStyle='primary' type='submit' />
          </div>
        </JsonForm>
      </div>
    </div>
  );
};

export const ApplicationForm = () => {
  const { applicationFormId } = useParams<string>();
  const [completed, setCompleted] = useState<boolean>(false);
  const [showInitialPage, setShowInitialPage] = useState(true);

  //Tanstack: version 5 query implementation
  const fetchApplicationForm = useQuery({
    queryKey: ['GET_APPLICATION_FORM_INFO'],
    queryFn: () => getApplicationFormInfo({ applicationFormId: applicationFormId! }),
  });
  const applicationForm = fetchApplicationForm?.data?.data;

  if (fetchApplicationForm.isLoading) {
    return <Loading loadingText='Loading' />;
  }

  if (!applicationForm) {
    return <PageNotFound />;
  }

  if (!applicationForm?.active) {
    return (
      <div className='flex justify-center px-4'>
        <div className='w-full max-w-xl bg-white border border-slate-200 rounded-2xl shadow-sm px-6 py-8 md:px-8 md:py-10'>
          {/* Heading */}
          <h1 className='text-xl md:text-2xl font-semibold text-slate-900'>This application is no longer active</h1>

          {/* Description */}
          {applicationForm?.formInactiveMessage ? (
            <p className='mt-2 text-sm md:text-base text-slate-500 leading-relaxed'>{applicationForm?.formInactiveMessage}</p>
          ) : (
            <p className='mt-2 text-sm md:text-base text-slate-500 leading-relaxed'>
              Access to this application has been discontinued. You can contact support if you need help.
            </p>
          )}
        </div>
      </div>
    );
  }

  const formConfig = formConfigurationMapping.find((fcm) => fcm.formCode === applicationForm.templateCode);

  if (!formConfig) {
    return (
      <PageNotFound
        title='Oops! Under Construction'
        subtitle='This page you are looking for is under construction. Please visit us again later.'
      />
    );
  }

  if ('applicationFormComponent' in formConfig && formConfig.applicationFormComponent) {
    return <formConfig.applicationFormComponent formData={fetchApplicationForm?.data} />;
  }

  if ('applicationFormInitialComponent' in formConfig && formConfig.applicationFormInitialComponent && showInitialPage) {
    return <formConfig.applicationFormInitialComponent startApplicationHandler={() => setShowInitialPage(false)} />;
  }

  if (completed) {
    return <SubmitSuccess message={applicationForm?.confirmationMessage} />;
  }

  let formSteps = [...standardSteps];
  if (formConfig.type === EFormConfigType.TEMPLATE && formConfig.formSteps) {
    formSteps = [...standardSteps, ...formConfig.formSteps];
  }

  let formSchema: any[] = [];

  if (formConfig.type === EFormConfigType.TEMPLATE && formConfig.validationSchema) {
    formSchema = [standardStepSchema, ...formConfig.validationSchema];
  }

  const initialValues: { [key: string]: any } = generateInitialValues(formSteps);

  return (
    <div className='flex flex-col justify-between'>
      <div className='container mx-auto p-5'>
        <h2 className='mb-0'>{applicationForm?.name}</h2>
      </div>
      <div className='bg-white h-full'>
        <ApplicationFormProvider initialValues={initialValues}>
          <ApplicationFormBase
            applicationFormId={applicationFormId!}
            steps={formSteps}
            validationSchema={formSchema}
            initialValues={initialValues}
            onSuccess={() => setCompleted(true)}
          />
        </ApplicationFormProvider>
      </div>
    </div>
  );
};
